export type RegionSpec =
  | { kind: 'between'; a: number; b: number }
  | { kind: 'leq'; a: number }
  | { kind: 'geq'; a: number }
  | { kind: 'tails'; a: number };

export function normalPdf(x: number, mu: number, sigma: number): number {
  const z = (x - mu) / sigma;
  return Math.exp(-0.5 * z * z) / (sigma * Math.sqrt(2 * Math.PI));
}

/** Abramowitz & Stegun 26.2.17; max error ≤ 1.5e-7 on |x| ≤ 8. */
export function normalCdf(x: number, mu: number, sigma: number): number {
  const z = (x - mu) / sigma;
  const sign = z < 0 ? -1 : 1;
  const absZ = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * absZ);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const poly = ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t;
  const erf = 1 - poly * Math.exp(-absZ * absZ);
  return 0.5 * (1 + sign * erf);
}

/** Beasley-Springer-Moro inverse normal CDF; max error ≤ 1e-9 for p ∈ [1e-15, 1-1e-15]. */
export function invNormalCdf(p: number, mu: number, sigma: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  const a = [
    -39.6968302866538, 220.946098424521, -275.928510446969, 138.357751867269, -30.6647980661472,
    2.50662827745924,
  ];
  const b = [
    -54.4760987982241, 161.585836858041, -155.698979859887, 66.8013118877197, -13.2806815528857,
  ];
  const c = [
    -0.00778489400243029, -0.322396458041136, -2.40075827716184, -2.54973253934373,
    4.37466414146497, 2.93816398269878,
  ];
  const d = [0.00778469570904146, 0.32246712907004, 2.445134137143, 3.75440866190742];
  const pLow = 0.02425;
  const pHigh = 1 - pLow;
  let z: number;
  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p));
    z =
      (((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1);
  } else if (p <= pHigh) {
    const q = p - 0.5;
    const r = q * q;
    z =
      ((((((a[0]! * r + a[1]!) * r + a[2]!) * r + a[3]!) * r + a[4]!) * r + a[5]!) * q) /
      (((((b[0]! * r + b[1]!) * r + b[2]!) * r + b[3]!) * r + b[4]!) * r + 1);
  } else {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    z =
      -(((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1);
  }
  return mu + sigma * z;
}

export function zScore(x: number, mu: number, sigma: number): number {
  return (x - mu) / sigma;
}

export function regionProbability(spec: RegionSpec, mu: number, sigma: number): number {
  switch (spec.kind) {
    case 'between':
      return Math.max(0, normalCdf(spec.b, mu, sigma) - normalCdf(spec.a, mu, sigma));
    case 'leq':
      return normalCdf(spec.a, mu, sigma);
    case 'geq':
      return 1 - normalCdf(spec.a, mu, sigma);
    case 'tails': {
      const inside = normalCdf(spec.a, mu, sigma) - normalCdf(-spec.a, mu, sigma);
      return 1 - inside;
    }
  }
}
